import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { VetDetailsPropTypes } from '../../../propTypes/vetDetails';
import { VetPracticesSearch } from '../vetPracticesSearch/vetPracticesSearch';
import { VetDetailsSummary } from '../vetDetailsSummary/vetDetailsSummary';
import { FullPetDetailsPropTypes } from '../../../propTypes/petDetails';
import './vetDetailPopup.scss';
import { VetDatesVisited } from '../vetDatesVisited/vetDatesVisited';
import { PetSelectCheckboxes } from '../petSelectCheckboxes/petSelectCheckboxes';
import { usePostClaimVetDetailsMutation, usePostPolicyVetDetailsMutation } from '../../../data/vets';
import { setNewlyAddedVets } from '../../../store/slice/vetDetailsSlice';
import { getPetsTreated, isValidDate } from '../../../helpers/vetHelpers';
import ErrorText from '../../shared/ErrorText/ErrorText';
import { SLICES } from '../../../constants/slices';

export const VetDetailPopup = ({
  petDetails,
  vetDetail,
  onCancel,
  setShowSuccessMessage,
  activePet,
  isClaimVetDetails
}) => {
  const dispatch = useDispatch();
  const vetDetailsList = useSelector((state) => state?.[SLICES.VET_DETAILS].vetDetailsList);
  const customerClaimId = useSelector((state) => state?.[SLICES.CLAIMS]?.customerClaimId);
  const { getValues, trigger, setValue, reset } = useFormContext();
  const isEditMode = vetDetail && vetDetail.ServiceProviderName;
  const [activeVet, setActiveVet] = useState(vetDetail);
  const [selectedPets, setSelectedPets] = useState([]); // Track an array of selected pets
  const [showPetError, setShowPetError] = useState(false);
  const [showAlreadyLinkedError, setShowAlreadyLinkedError] = useState(false);
  const [postPolicyVetDetails, { isSuccess: postPolicyVetDetailsSuccess }] = usePostPolicyVetDetailsMutation();
  const [postClaimVetDetails, { isSuccess: postClaimVetDetailsSuccess }] = usePostClaimVetDetailsMutation();

  useEffect(() => {
    if (vetDetail && vetDetail.PetsTreated?.length > 0) {
      const selected = vetDetail.PetsTreated.map((pet) => {
        const selectedPetDetails = petDetails.find((petDetail) => petDetail.PolicyNumber === pet.PolicyNumber);
        if (pet.FromDate && isValidDate(pet.FromDate)) {
          const fromDate = new Date(pet.FromDate);
          setValue(`fromMonth-${pet.PolicyNumber}`, fromDate.getMonth());
          setValue(`fromYear-${pet.PolicyNumber}`, fromDate.getFullYear());
        }

        if (pet.ToDate && isValidDate(pet.ToDate)) {
          const toDate = new Date(pet.ToDate);
          setValue(`toMonth-${pet.PolicyNumber}`, toDate.getMonth());
          setValue(`toYear-${pet.PolicyNumber}`, toDate.getFullYear());
        }

        return {
          name: pet.PetName,
          policyNumber: pet.PolicyNumber,
          selected: true,
          dateOfBirth: selectedPetDetails.DateOfBirth
        };
      });

      setSelectedPets(selected);
    }
  }, [vetDetail, petDetails, setValue]);

  useEffect(() => {
    if (activePet) {
      const selected = {
        name: activePet.Name,
        policyNumber: activePet.PolicyNumber,
        dateOfBirth: activePet.DateOfBirth,
        selected: true
      };
      setSelectedPets([selected]);
    }
  }, [activePet]);

  const handleVetSelect = (vet) => {
    setActiveVet(vet);
  };

  const handlePetSelect = (policyNumber, name, dateOfBirth, selected) => {
    if (selected) {
      setSelectedPets((prevActivePets) => [...prevActivePets, { policyNumber, name, dateOfBirth, selected }]);
      setShowPetError(false);
    } else {
      setSelectedPets((prevActivePets) => {
        const updatedPets = prevActivePets.filter((pet) => pet.policyNumber !== policyNumber);
        setShowPetError(updatedPets.length === 0);
        return updatedPets;
      });
    }
  };

  const handleSubmit = async () => {
    const fieldNames = ['practiceName', 'petSelectCheckbox'];
    selectedPets?.forEach((pet) => {
      if (pet.selected) {
        fieldNames.push(`fromMonth-${pet.policyNumber}`);
        fieldNames.push(`fromYear-${pet.policyNumber}`);
        fieldNames.push(`toMonth-${pet.policyNumber}`);
        fieldNames.push(`toYear-${pet.policyNumber}`);
      }
    });

    const isValid = await trigger(fieldNames);

    if (!activeVet) {
      return;
    }

    if (selectedPets.length === 0) {
      setShowPetError(true);
      return;
    }

    if (isValid && !showAlreadyLinkedError) {
      setShowPetError(false);

      const formValues = getValues();

      // append any existing pets treated of the activeVet service provider number
      const existingPetTreated =
        vetDetailsList?.find((vet) => vet.ServiceProviderNumber === activeVet.serviceProviderNumber)?.PetsTreated ?? [];

      const petsTreated = [
        ...(existingPetTreated ?? []),
        ...(selectedPets?.map((pet) => getPetsTreated(pet, formValues, isClaimVetDetails, activeVet, vetDetailsList)) ??
          [])
      ];

      if (isClaimVetDetails && activeVet.VetId !== 0) {
        const claimPolicyVets = {
          VetId: activeVet.VetId,
          CustomerClaimId: customerClaimId,
          PracticeName: vetDetail ? vetDetail.ServiceProviderName : activeVet.serviceProvider,
          PracticeNumber: null,
          ServiceProviderNumber: vetDetail ? vetDetail.ServiceProviderNumber : activeVet.serviceProviderNumber,
          PracticeAddress: vetDetail ? vetDetail.ServiceProviderAddress : activeVet.AddressLine1,
          Suburb: vetDetail ? vetDetail.Suburb : activeVet.suburb,
          State: vetDetail ? vetDetail.State : activeVet.state,
          PostCode: vetDetail ? vetDetail.Postcode : activeVet.postCode,
          ContactNumber: vetDetail ? vetDetail.ServiceProviderContactNumber : activeVet.phoneNumber,
          PracticeEmailAddress: null,
          IsUpdateRequest: false,
          IsUPMVet: false,
          PetsTreated: petsTreated
        };
        if (customerClaimId) {
          postClaimVetDetails(claimPolicyVets);
        } else {
          dispatch(setNewlyAddedVets({ activeVet, petsTreated }));
          onCancel();
        }
      } else {
        const policyVets = {
          ServiceProviderNumber: vetDetail ? vetDetail.ServiceProviderNumber : activeVet.serviceProviderNumber,
          PracticeName: vetDetail ? vetDetail.ServiceProviderName : activeVet.serviceProvider,
          PracticeAddress: vetDetail ? vetDetail.ServiceProviderAddress : activeVet.AddressLine1,
          Suburb: vetDetail ? vetDetail.Suburb : activeVet.suburb,
          State: vetDetail ? vetDetail.State : activeVet.state,
          PostCode: vetDetail ? vetDetail.Postcode : activeVet.postCode,
          ContactNumber: vetDetail ? vetDetail.ServiceProviderContactNumber : activeVet.phoneNumber,
          PracticeEmailAddress: null,
          IsUpdateRequest: false,
          IsUPMVet: false,
          PetsTreated: petsTreated
        };
        postPolicyVetDetails(policyVets);
      }
    }
  };

  useEffect(() => {
    if (postPolicyVetDetailsSuccess || postClaimVetDetailsSuccess) {
      reset();
      onCancel();
      if (postPolicyVetDetailsSuccess) {
        setShowSuccessMessage(postPolicyVetDetailsSuccess);
      }
    }
  }, [postPolicyVetDetailsSuccess, postClaimVetDetailsSuccess, onCancel, setShowSuccessMessage, reset]);

  const handleClose = () => {
    reset();
    onCancel();
  };
  return (
    <Modal
      size="small"
      isContentScrollable
      showFooterDivider
      open
      onClose={handleClose}
      header={<h2 className="modal-title">{isEditMode ? 'Update vet details' : 'Add your vet'}</h2>}
      footer={
        <div className="footerModalContainer">
          <button
            type="button"
            className="button primary outline"
            onClick={handleClose}
          >
            <span className="labelText">Cancel</span>
          </button>

          <button
            type="button"
            className="button primary"
            onClick={handleSubmit}
          >
            <span className="labelText">{vetDetail ? 'Update' : 'Add Vet'}</span>
          </button>
        </div>
      }
    >
      <div className="vetDetailPopup">
        {!isEditMode && (
          <VetPracticesSearch
            onSelect={handleVetSelect}
            selectedPets={selectedPets}
            activeVet={activeVet}
            showAlreadyLinkedError={showAlreadyLinkedError}
            setShowAlreadyLinkedError={setShowAlreadyLinkedError}
          />
        )}

        <VetDetailsSummary
          vetDetail={activeVet || {}}
          petDetails={petDetails}
        />
        <h4>Select the pet&#47;s which this vet treated.</h4>
        {petDetails?.map((pet) => (
          <div key={pet.PolicyNumber}>
            <PetSelectCheckboxes
              petDetails={[pet]}
              onSelect={handlePetSelect}
              selectedPets={selectedPets}
              isClaimVetDetails={isClaimVetDetails}
            />
            {selectedPets?.map(
              (selectedPet) =>
                selectedPet?.policyNumber === pet.PolicyNumber &&
                selectedPet?.selected && (
                  <VetDatesVisited
                    key={selectedPet?.policyNumber}
                    policyNumber={selectedPet?.policyNumber}
                    dateOfBirth={selectedPet?.dateOfBirth}
                  />
                )
            )}
          </div>
        ))}
        <ErrorText
          cssClass="generic_error checkBoxError"
          message={[{ value: 'Please select a pet.', isDisplayed: showPetError }]}
        />
      </div>
    </Modal>
  );
};

VetDetailPopup.propTypes = {
  petDetails: PropTypes.arrayOf(FullPetDetailsPropTypes),
  vetDetail: PropTypes.oneOfType([PropTypes.bool, VetDetailsPropTypes]),
  onCancel: PropTypes.func,
  setShowSuccessMessage: PropTypes.func,
  activePet: FullPetDetailsPropTypes,
  isClaimVetDetails: PropTypes.bool
};
